import { useState, useEffect } from 'react';
import { Ticket, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import pb from '../lib/pocketbase';

// Liste des services disponibles
const SERVICES = [
  { id: 'base_oneshot', label: 'Site Clé en main', emoji: '🏠', category: 'Offre de base' },
  { id: 'base_saas', label: 'Abonnement mensuel', emoji: '📅', category: 'Offre de base' },
  { id: 'logo', label: 'Logo professionnel', emoji: '🎨', category: 'Options' },
  { id: 'blog', label: 'Blog / Actualités', emoji: '📝', category: 'Options' },
  { id: 'form', label: 'Formulaire de devis', emoji: '📋', category: 'Options' },
  { id: 'domain', label: 'Nom de domaine', emoji: '🌐', category: 'Options' },
  { id: 'gmb_creation', label: 'Création fiche Google', emoji: '📍', category: 'Google' },
  { id: 'gmb_optim', label: 'Optimisation fiche Google', emoji: '⚡', category: 'Google' },
  { id: 'visibility', label: 'Pack Visibilité', emoji: '🚀', category: 'Visibilité' },
];

interface PromoCode {
  id: string;
  code: string;
  reduction_percent: number;
  applies_to: 'oneshot' | 'saas' | 'both' | null;
  excluded_services: string[] | null;
  max_uses: number | null;
  uses_count: number;
  expires_at: string | null;
  description: string;
  active: boolean;
  created: string;
}

export default function PromoCodes() {
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    reduction_percent: 10,
    applies_to: 'both' as 'oneshot' | 'saas' | 'both',
    excluded_services: [] as string[],
    max_uses: '',
    expires_at: '',
    description: '',
    active: true
  });

  useEffect(() => {
    loadPromos();
  }, []);

  const loadPromos = async () => {
    try {
      setLoading(true);
      const records = await pb.collection('promo_codes').getFullList<PromoCode>();
      setPromos(records);
    } catch (err: any) {
      console.error('[PromoCodes] Error:', err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  const openNewModal = () => {
    setEditingPromo(null);
    setFormData({
      code: '',
      reduction_percent: 10,
      applies_to: 'both',
      excluded_services: [],
      max_uses: '',
      expires_at: '',
      description: '',
      active: true
    });
    setShowModal(true);
  };

  const openEditModal = (promo: PromoCode) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      reduction_percent: promo.reduction_percent,
      applies_to: promo.applies_to || 'both',
      excluded_services: promo.excluded_services || [],
      max_uses: promo.max_uses?.toString() || '',
      expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : '',
      description: promo.description || '',
      active: promo.active !== false
    });
    setShowModal(true);
  };

  const toggleService = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      excluded_services: prev.excluded_services.includes(serviceId)
        ? prev.excluded_services.filter(s => s !== serviceId)
        : [...prev.excluded_services, serviceId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        code: formData.code.toUpperCase().trim(),
        reduction_percent: formData.reduction_percent,
        applies_to: formData.applies_to,
        excluded_services: formData.excluded_services.length > 0 ? formData.excluded_services : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null,
        description: formData.description.trim(),
        active: formData.active
      };

      if (editingPromo) {
        await pb.collection('promo_codes').update(editingPromo.id, data);
      } else {
        await pb.collection('promo_codes').create({ ...data, uses_count: 0 });
      }

      setShowModal(false);
      loadPromos();
    } catch (err) {
      console.error('Error saving promo:', err);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deletePromo = async (id: string, code: string) => {
    if (!confirm(`Supprimer le code "${code}" ?`)) return;

    try {
      await pb.collection('promo_codes').delete(id);
      setPromos(promos.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting promo:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (promo: PromoCode) => {
    try {
      await pb.collection('promo_codes').update(promo.id, { active: !promo.active });
      setPromos(promos.map(p => p.id === promo.id ? { ...p, active: !p.active } : p));
    } catch (err) {
      console.error('Error toggling promo:', err);
    }
  };

  const getAppliesToLabel = (appliesTo: string | null) => {
    switch (appliesTo) {
      case 'oneshot': return 'Clé en main';
      case 'saas': return 'Abonnement';
      default: return 'Tout';
    }
  };

  const getExcludedLabel = (excluded: string[] | null) => {
    if (!excluded || excluded.length === 0) return null;
    const labels = excluded.map(id => SERVICES.find(s => s.id === id)?.label || id);
    return labels.join(', ');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <Ticket className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Codes promo</h1>
            <p className="text-sm text-gray-500">{promos.length} code{promos.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={openNewModal}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Nouveau code
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">
          Chargement...
        </div>
      ) : promos.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">
          Aucun code promo. Créez-en un !
        </div>
      ) : (
        <div className="space-y-3">
          {promos.map(promo => (
            <div
              key={promo.id}
              className={`bg-white rounded-xl p-4 border border-gray-100 hover:border-primary-200 transition-colors ${!promo.active ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-primary-500 text-white font-mono font-bold rounded-lg text-sm">
                      {promo.code}
                    </span>
                    <span className="text-lg font-semibold text-green-600">
                      {promo.reduction_percent === 100 ? 'GRATUIT' : `-${promo.reduction_percent}%`}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${promo.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {promo.active ? '✓ Actif' : 'Inactif'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                    <span>📦 {getAppliesToLabel(promo.applies_to)}</span>
                    <span>🔢 {promo.max_uses ? `${promo.uses_count}/${promo.max_uses}` : `${promo.uses_count} utilisations`}</span>
                    <span>📅 {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString('fr-FR') : 'Pas d\'expiration'}</span>
                    {promo.description && <span className="text-gray-400">• {promo.description}</span>}
                  </div>
                  {promo.excluded_services && promo.excluded_services.length > 0 && (
                    <div className="mt-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded inline-block">
                      ⚠️ Exclut : {getExcludedLabel(promo.excluded_services)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(promo)}
                    className={`p-2 rounded-lg transition-colors ${promo.active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                    title={promo.active ? 'Désactiver' : 'Activer'}
                  >
                    {promo.active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(promo)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deletePromo(promo.id, promo.code)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8">
            <h2 className="text-lg font-semibold mb-4">
              {editingPromo ? 'Modifier le code' : 'Nouveau code promo'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase font-mono"
                  placeholder="BIENVENUE20"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Réduction (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.reduction_percent}
                    onChange={e => setFormData({ ...formData, reduction_percent: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">S'applique à</label>
                  <select
                    value={formData.applies_to}
                    onChange={e => setFormData({ ...formData, applies_to: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="both">Toutes les offres</option>
                    <option value="oneshot">Clé en main uniquement</option>
                    <option value="saas">Abonnement uniquement</option>
                  </select>
                </div>
              </div>

              {/* Services exclus */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Services exclus de la réduction
                  <span className="font-normal text-gray-400 ml-1">(optionnel)</span>
                </label>
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="grid grid-cols-2 gap-2">
                    {SERVICES.map(service => (
                      <label
                        key={service.id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                          formData.excluded_services.includes(service.id)
                            ? 'bg-orange-100 border border-orange-300'
                            : 'bg-white border border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.excluded_services.includes(service.id)}
                          onChange={() => toggleService(service.id)}
                          className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm">
                          {service.emoji} {service.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formData.excluded_services.length > 0 && (
                    <p className="mt-2 text-xs text-orange-600">
                      ⚠️ {formData.excluded_services.length} service(s) exclu(s) — le client paiera le prix normal pour ces services
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utilisations max</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.max_uses}
                    onChange={e => setFormData({ ...formData, max_uses: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Illimité"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'expiration</label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={e => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (visible par le client)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Ex: Code de bienvenue"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={e => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Code actif</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
